import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  Store,
  PieChart,
  ShoppingBag,
  FileText,
  CheckCircle,
  DollarSign,
  Database,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  LogOut,
  UserRound,
  MessageSquare,
  Tags,
  Activity,
  Calculator,
  Boxes
} from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../store/hooks/reduxHooks';
import { handleLogout } from '../../utils/sessionManager';
import { UserRole, Permission } from '../../types/auth';
import { fetchKycPendingMerchantsThunk } from '../../features/kycPendingMerchants/merchantKYCSlice';
// KYC Pending Merchant count from Redux



// Logo import
import blackLogo from '../../assets/CleverPe Logos/CleverPe_Black_H.svg';
// import path from 'path';

// Type definitions
type SidebarItem = {
  title: string;
  path: string;
  icon: React.ReactNode;
  requiredPermission: Permission;
  badgeCount?: number;
};

type SidebarSection = {
  title: string;
  items: SidebarItem[];
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'Overview',
    'BUSINESS NETWORK',
    'ADMIN & ACCESS',
    'Customers',
    'PRODUCTS & SCHEMES',
    'Reports',
    'API Monitoring',
    'ENQUIRIES & SUPPORT',
    'TOOLS',
    'ORDERS & TRANSACTIONS'
  ]);

  const kycPendingTotal = useAppSelector(state => state.merchantKYC.total);
  const kycPendingHasFetched = useAppSelector(state => state.merchantKYC.hasFetched);

  useEffect(() => {
    if (!kycPendingHasFetched) {
      dispatch(fetchKycPendingMerchantsThunk());
    }
  }, [dispatch, kycPendingHasFetched]);


  // Get the role prefix for routes based on user role
  const rolePrefix = useMemo(() => {
    if (!user) return ''; // Default fallback

    switch (user.role) {
      case UserRole.ADMIN:
        return '/admin';
      case UserRole.SUPERADMIN:
        return '/superadmin';
      case UserRole.CLEVERPE_USER:
        return '/cleverpeuser';
      default:
        return '';
    }
  }, [user]);

  // Get dashboard path based on user role
  const dashboardPath = useMemo(() => {
    return `${rolePrefix}/dashboard`;
  }, [rolePrefix]);

  // Check if user has required permission
  const hasPermission = (permission: Permission): boolean => {
    if (!user?.dashPermissions) return false;
    return user.dashPermissions.includes(permission);
  };

  // Sidebar data structure with sections and items
  const sidebarData = useMemo<SidebarSection[]>(() => {
    // For backward compatibility, check if user has role-based access or permissions
    const isCleverpeUser = user?.role === UserRole.CLEVERPE_USER;

    // Admin management items (only show if user has permission)
    const adminItems = hasPermission('view-manage-admin') ? [
      {
        title: "Manage Admins",
        path: `${rolePrefix}/manage-admins`,
        icon: <Users size={16} />,
        requiredPermission: 'view-manage-admin' as Permission
      }
    ] : [];

    // CleverPe Supply+ item
    const cleverpeSupplyItems = hasPermission('view-cleverpe-supply+') ? [
      {
        title: "CleverPe Supply+",
        path: `${rolePrefix}/manage-cleverpe-supply`,
        icon: <CheckCircle size={16} />,
        requiredPermission: 'view-cleverpe-supply+' as Permission
      }
    ] : [];

    // Business section items
    const businessItems = [
      {
        title: "OEM/Partners",
        path: isCleverpeUser ? `${rolePrefix}/partners` : `${rolePrefix}/manage-partner`,
        icon: <Building2 size={16} />,
        requiredPermission: 'view-partner' as Permission
      },
      {
        title: "Merchants",
        path: isCleverpeUser ? `${rolePrefix}/merchants` : `${rolePrefix}/manage-merchant`,
        icon: <Store size={16} />,
        requiredPermission: 'view-merchant' as Permission
      },
      {
        title: 'KYC Pending Merchants',
        path: isCleverpeUser
          ? `${rolePrefix}/merchants-kyc-pending`
          : `${rolePrefix}/manage-merchant-kyc-pending`,
        icon: <Store size={16} />,
        requiredPermission: 'view-kycpending-merchant' as Permission,
        badgeCount: kycPendingTotal
      },
      {
        title: 'Lender Merchant Approval',
        path: `${rolePrefix}/manage-merchant-approval`,
        icon: <CheckCircle size={16} />,
        requiredPermission: 'view-lender-merchant-approval' as Permission
      }
    ].filter(item => hasPermission(item.requiredPermission));

    // Customers section items
    const customerItems = [
      {
        title: "Customers",
        path: isCleverpeUser ? `${rolePrefix}/customers` : `${rolePrefix}/manage-customers`,
        icon: <UserRound size={16} />,
        requiredPermission: 'view-customer' as Permission
      },
      {
        title: "Pre Approval Logs",
        path: `${rolePrefix}/pre-approval-logs`,
        icon: <CheckCircle size={16} />,
        requiredPermission: 'view-store-approvals' as Permission
      }
    ].filter(item => hasPermission(item.requiredPermission));

    // Products section items
    const servicesItems = [
      {
        title: "Products",
        path: isCleverpeUser ? `${rolePrefix}/products` : `${rolePrefix}/manage-product`,
        icon: <ShoppingBag size={16} />,
        requiredPermission: 'view-product' as Permission
      },
      {
        title: "Categories",
        path: isCleverpeUser ? `${rolePrefix}/categories` : `${rolePrefix}/manage-category`,
        icon: <Tags size={16} />,
        requiredPermission: 'view-category' as Permission
      },
      {
        title: "Schemes",
        path: isCleverpeUser ? `${rolePrefix}/schemes` : `${rolePrefix}/manage-scheme`,
        icon: <PieChart size={16} />,
        requiredPermission: 'view-scheme' as Permission
      },
      {
        title: "Scheme Buckets",
        path: isCleverpeUser ? `${rolePrefix}/scheme-buckets` : `${rolePrefix}/manage-scheme-bucket`,
        icon: <Boxes size={16} />,
        requiredPermission: 'view-scheme-bucket' as Permission
      },
      // {
      //   title: "Cancel Orders",
      //   path: isCleverpeUser ? `${rolePrefix}/cancel-orders` : `${rolePrefix}/manage-cancel-order`,
      //   icon: <FileText size={16} />,
      //   requiredPermission: 'view-cancel-order' as Permission
      // }
    ].filter(item => hasPermission(item.requiredPermission));

    const orderItems = [
      {
        title: "Orders",
        path: isCleverpeUser ? `${rolePrefix}/orders` : `${rolePrefix}/manage-order`,
        icon: <FileText size={16} />,
        requiredPermission: 'view-order' as Permission
      },
      // {
      //   title: "Gateway Orders",
      //   path: isCleverpeUser ? `${rolePrefix}/gateway-orders` : `${rolePrefix}/manage-gateway-order`,
      //   icon: <FileText size={16} />,
      //   requiredPermission: 'view-gateway-order' as Permission
      // },
    ].filter(item => hasPermission(item.requiredPermission));

    const calculatorItems = [
      {
        title: "EMI Calculator",
        path: `${rolePrefix}/calculator`,
        icon: <Calculator size={16} />,
        requiredPermission: 'view-calculator' as Permission
      }
    ].filter(item => hasPermission(item.requiredPermission));

    // Reports section items
    const reportsItems = [
      {
        title: "Pre-Approvals",
        path: `${rolePrefix}/reports-pre-approvals`,
        icon: <CheckCircle size={16} />,
        requiredPermission: 'view-pre-approvals-report' as Permission
      },
      {
        title: "Settlements",
        path: `${rolePrefix}/reports-settlements`,
        icon: <DollarSign size={16} />,
        requiredPermission: 'view-settlements-report' as Permission
      },
      {
        title: "Merchant Data",
        path: `${rolePrefix}/reports-merchant-data`,
        icon: <Database size={16} />,
        requiredPermission: 'view-merchant-data-report' as Permission
      }
    ].filter(item => hasPermission(item.requiredPermission));

    const apiMonitoringItems = [
      {
        title: "External API Logs",
        path: `${rolePrefix}/external-logs`,
        icon: <Activity size={16} />,
        requiredPermission: 'view-external-api-logs' as Permission
      }
    ].filter(item => hasPermission(item.requiredPermission));


    const messagesMenuItems = [
      {
        title: "Contact Messages",
        path: `${rolePrefix}/grievance`,
        icon: <MessageSquare size={16} />,
        requiredPermission: 'view-grievance-redressals' as Permission
      },

    ].filter(item => hasPermission(item.requiredPermission));

    // Build the sidebar sections based on available items
    return [
      {
        title: "Overview",
        items: [
          {
            title: "Dashboard",
            path: dashboardPath,
            icon: <LayoutDashboard size={16} />,
            requiredPermission: 'view-dashboard' as Permission
          },
          // ...adminItems,
          ...cleverpeSupplyItems
        ].filter(item => hasPermission(item.requiredPermission))
      },
      {
        title: "ADMIN & ACCESS",
        items: [
          ...adminItems
        ]
      },
      {
        title: "BUSINESS NETWORK",
        items: businessItems
      },
      {
        title: "Customers",
        items: customerItems
      },
      {
        title: "ORDERS & TRANSACTIONS",
        items: orderItems
      },
      {
        title: "PRODUCTS & SCHEMES",
        items: servicesItems
      },
      {
        title: "TOOLS",
        items: calculatorItems
      },
      {
        title: "Reports",
        items: reportsItems
      },
      {
        title: "ENQUIRIES & SUPPORT",
        items: messagesMenuItems
      },
      {
        title: "API Monitoring",
        items: apiMonitoringItems
      },
    ];
  }, [rolePrefix, dashboardPath, user, kycPendingTotal]);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Check if a route is active
  const isActiveRoute = (path: string) => {
    if (!path) return false;
    return location.pathname === path ||
      location.pathname.startsWith(`${path}/`) ||
      // Also check base paths without role prefix
      location.pathname.endsWith(path.split('/').pop() || '');
  };

  // Navigate to a route and close mobile sidebar
  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  // Handle logout
  const handleLogoutClick = () => {
    handleLogout(dispatch);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile toggle button */}
      <button
        className="fixed z-30 p-2 bg-white rounded-md shadow-md top-4 left-4 lg:hidden"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar - Updated to be fixed position on all devices */}
      <aside
        className={`fixed inset-y-0 left-0 z-20 w-64 bg-white shadow-xl transition-transform duration-300 transform 
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 flex flex-col h-screen`}
      >
        {/* Logo and header */}
        <div className="flex items-center justify-center h-16 py-2 border-b">
          <img src={blackLogo} alt="CleverPe" className="h-10" />
        </div>

        {/* Sidebar scrollable content with hidden scrollbar */}
        <div className="flex-1 overflow-y-auto no-scrollbar">
          <nav className="p-3">
            {sidebarData.map((section) => (
              section.items.length > 0 && (
                <div key={section.title} className="mb-3">
                  {/* Section header */}
                  <button
                    className="flex items-center justify-between w-full mb-1 text-left"
                    onClick={() => toggleSection(section.title)}
                  >
                    <h3 className="text-xs tracking-wider uppercase font-gilroy-bold text-brand-gray">
                      {section.title}
                    </h3>
                    {expandedSections.includes(section.title) ? (
                      <ChevronDown size={14} className="text-brand-gray" />
                    ) : (
                      <ChevronRight size={14} className="text-brand-gray" />
                    )}
                  </button>

                  {/* Section items */}
                  {expandedSections.includes(section.title) && (
                    <ul className="space-y-0.5 mb-2">
                      {section.items.map((item) => (
                        <li key={item.path}>
                          <button
                            className={`flex items-center w-full px-3 py-2 rounded-lg transition-colors duration-200 text-sm
                              ${isActiveRoute(item.path)
                                ? 'bg-brand-light text-brand-blue font-gilroy-medium'
                                : 'text-brand-dark hover:bg-gray-100 font-gilroy-regular'
                              }`}
                            onClick={() => handleNavigation(item.path)}
                          >
                            <span className="mr-2.5">
                              {item.icon}
                            </span>
                            <span className="flex items-center gap-2 whitespace-nowrap">
                              <span className="truncate">{item.title}</span>

                              {item.badgeCount && item.badgeCount > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-2 text-xs font-medium rounded-full bg-red-100 text-red-600">
                                  {item.badgeCount}
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Section divider */}
                  {section.title !== sidebarData[sidebarData.length - 1].title &&
                    section.items.length > 0 && (
                      <div className="my-1 border-b border-gray-200"></div>
                    )}
                </div>
              )
            ))}
          </nav>
        </div>

        {/* Logout button */}
        <div className="p-3 border-t">
          <button
            onClick={handleLogoutClick}
            className="flex items-center w-full px-3 py-2 text-sm text-red-600 transition-colors duration-200 rounded-lg hover:bg-red-50"
          >
            <span className="mr-2.5">
              <LogOut size={16} />
            </span>
            Logout
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar; 